/**
 * /admin/artifacts — operator-tunable run-artifact retention (#662
 * slice 5).
 *
 * Single knob (`maxAgeDays`) + status panel showing the bucket's
 * actual lifecycle state and cached bucket stats. Apply writes two
 * layers (DB then S3); a partial-success (DB committed but S3 sync
 * failed) surfaces a "Retry sync" affordance — the DB value is still
 * the source of truth for the backend's serving cap, so transient
 * drift between the layers is operator-resolvable.
 *
 * Two storage choices, two panels. On S3 (our hosted instance) the page
 * shows the bucket, its expiry rule and "Retry sync". On disk (every
 * self-hosted install, and local dev) there is no bucket: the page shows
 * the folder, says the daily Run file cleanup job deletes runs past the
 * limit, and counts disk use. `store` from the API picks the panel.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@/components/ui'
import {
  adminRunArtifactsAPI,
  type AdminRunArtifactsRetention,
} from '@/lib/api'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatLocalDateTime } from '@/lib/utils'

export function ArtifactsPage() {
  const tz = useUserTimezone()
  const [data, setData] = useState<AdminRunArtifactsRetention | null>(null)
  const [draft, setDraft] = useState<string>('')
  const [traceDraft, setTraceDraft] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingTrace, setSavingTrace] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [traceError, setTraceError] = useState<string | null>(null)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const res = await adminRunArtifactsAPI.getRetention()
      setData(res)
      setDraft(String(res.maxAgeDays))
      setTraceDraft(String(res.traceRetentionDays))
      setLifecycleError(res.lifecycleError ?? null)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load retention')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function handleApply(evt: FormEvent<HTMLFormElement>): Promise<void> {
    evt.preventDefault()
    if (!data) return
    const parsed = Number(draft)
    if (
      !Number.isFinite(parsed) ||
      !Number.isInteger(parsed) ||
      parsed < data.range.min ||
      parsed > data.range.max
    ) {
      setError(`maxAgeDays must be an integer in [${data.range.min}, ${data.range.max}]`)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const next = await adminRunArtifactsAPI.setRetention({ maxAgeDays: parsed })
      setData(next)
      setDraft(String(next.maxAgeDays))
      setLifecycleError(next.lifecycleError ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update retention')
    } finally {
      setSaving(false)
    }
  }

  async function handleApplyTrace(
    evt: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    evt.preventDefault()
    if (!data) return
    const parsed = Number(traceDraft)
    if (
      !Number.isFinite(parsed) ||
      !Number.isInteger(parsed) ||
      parsed < data.traceRange.min ||
      parsed > data.traceRange.max
    ) {
      setTraceError(
        `Enter a whole number of days between ${data.traceRange.min} and ${data.traceRange.max}`,
      )
      return
    }
    setSavingTrace(true)
    setTraceError(null)
    try {
      const next = await adminRunArtifactsAPI.setRetention({
        traceRetentionDays: parsed,
      })
      setData(next)
      setTraceDraft(String(next.traceRetentionDays))
      setLifecycleError(next.lifecycleError ?? null)
    } catch (e) {
      setTraceError(e instanceof Error ? e.message : 'Failed to update')
    } finally {
      setSavingTrace(false)
    }
  }

  async function handleRetrySync(): Promise<void> {
    if (!data) return
    // Re-apply the persisted value — backend re-issues the S3 Put.
    setSaving(true)
    setError(null)
    try {
      const next = await adminRunArtifactsAPI.setRetention({
        maxAgeDays: data.maxAgeDays,
      })
      setData(next)
      setLifecycleError(next.lifecycleError ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to retry sync')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading retention settings…</p>
  }
  if (error && !data) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }
  if (!data) return null

  const dirty = String(data.maxAgeDays) !== draft
  const traceDirty = String(data.traceRetentionDays) !== traceDraft
  const isDisk = data.store === 'disk'
  const marginDays = data.maxAgeDays - data.softCapDays

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Run artifact retention</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleApply} className="space-y-4">
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="maxAgeDays">Keep run artifacts for</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="maxAgeDays"
                    type="number"
                    min={data.range.min}
                    max={data.range.max}
                    step={1}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="w-32"
                    disabled={saving}
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>
              <Button type="submit" disabled={saving || !dirty}>
                {saving ? 'Applying…' : 'Apply'}
              </Button>
              {data.source === 'default' && (
                <span className="text-xs text-muted-foreground italic">
                  using built-in default
                </span>
              )}
            </div>

            {isDisk ? (
              <p className="text-xs text-muted-foreground">
                Range {data.range.min} – {data.range.max}. A daily cleanup
                deletes finished runs older than this from this server's disk. Their
                reports stop opening at {data.softCapDays} days,{' '}
                {marginLabel(marginDays)} early, as a safety margin. The runs
                stay in your run history.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Range {data.range.min} – {data.range.max}. Applied as BOTH an S3
                lifecycle rule (deletes objects past the cutoff) and a backend
                serving cap (refuses to serve objects older than{' '}
                {Math.max(1, data.maxAgeDays - 7)} days, a one-week safety
                margin against a buggy lifecycle config).
              </p>
            )}

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trace file cleanup</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleApplyTrace} className="space-y-4">
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="traceRetentionDays">
                  Keep Playwright trace files for
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="traceRetentionDays"
                    type="number"
                    min={data.traceRange.min}
                    max={data.traceRange.max}
                    step={1}
                    value={traceDraft}
                    onChange={(e) => setTraceDraft(e.target.value)}
                    className="w-32"
                    disabled={savingTrace}
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>
              <Button type="submit" disabled={savingTrace || !traceDirty}>
                {savingTrace ? 'Applying…' : 'Apply'}
              </Button>
              {data.traceSource === 'default' && (
                <span className="text-xs text-muted-foreground italic">
                  using built-in default
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Traces are the heavy part of a run — they power the deep
              step-by-step viewer but take the most space. A daily cleanup
              job deletes trace files older than this, while keeping each
              run's report so it still opens (you just lose the trace
              drill-down). This is usually shorter than the{' '}
              {data.maxAgeDays}-day window above, which removes the whole run.
              Range {data.traceRange.min} – {data.traceRange.max} days.
            </p>

            {traceError && (
              <p className="text-sm text-destructive" role="alert">
                {traceError}
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Storage state</CardTitle>
        </CardHeader>
        <CardContent>
          {isDisk ? (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Where files are kept</dt>
              <dd>
                This server's disk
                {data.diskFolder && (
                  <>
                    , in <code className="break-all">{data.diskFolder}</code>
                  </>
                )}
              </dd>

              <dt className="text-muted-foreground">Old runs</dt>
              <dd>
                Finished runs are deleted every day once older than{' '}
                {data.maxAgeDays} days, by the{' '}
                <Link
                  to="/admin/scheduled-jobs/traceRetentionSweep"
                  className="text-primary hover:underline"
                >
                  Run file cleanup
                </Link>{' '}
                job
              </dd>

              <dt className="text-muted-foreground">Reports stop opening</dt>
              <dd>
                After {data.softCapDays} days, {marginLabel(marginDays)} before
                the files are deleted
              </dd>
            </dl>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Bucket</dt>
              <dd>
                {data.bucket ?? (
                  <span className="text-muted-foreground italic">
                    Disk store (local dev) — no bucket
                  </span>
                )}
              </dd>

              <dt className="text-muted-foreground">S3 lifecycle</dt>
              <dd>{renderLifecycle(data.lifecycle, data.maxAgeDays)}</dd>

              <dt className="text-muted-foreground">Backend serving cap</dt>
              <dd>
                Refuses to serve &gt; {data.softCapDays} days (margin{' '}
                {data.maxAgeDays - data.softCapDays}d)
              </dd>
            </dl>
          )}

          {!isDisk && lifecycleError && (
            <div
              className="mt-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm"
              role="alert"
            >
              <p className="font-medium text-destructive">S3 sync failed</p>
              <p className="mt-1 text-destructive/90 break-all">
                {lifecycleError}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                The DB value is committed — the backend serving cap honors it.
                The S3 lifecycle rule is unchanged from before this update;
                retry will re-issue the PutBucketLifecycleConfiguration call.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void handleRetrySync()}
                disabled={saving}
              >
                {saving ? 'Retrying…' : 'Retry sync'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{isDisk ? 'Disk use' : 'Bucket statistics'}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">{isDisk ? 'Files' : 'Objects'}</dt>
            <dd>{data.stats.objects.toLocaleString()}</dd>

            <dt className="text-muted-foreground">Total size</dt>
            <dd>{formatBytes(data.stats.totalBytes)}</dd>

            <dt className="text-muted-foreground">{isDisk ? 'Oldest file' : 'Oldest object'}</dt>
            <dd>
              {data.stats.oldestObjectAt
                ? `${formatRelative(data.stats.oldestObjectAt)} (${formatLocalDateTime(data.stats.oldestObjectAt, tz)})`
                : '—'}
            </dd>

            <dt className="text-muted-foreground">{isDisk ? 'Largest file' : 'Largest object'}</dt>
            <dd>
              {data.stats.largestObjectBytes !== undefined
                ? formatBytes(data.stats.largestObjectBytes)
                : '—'}
            </dd>
          </dl>
          {isDisk ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Counted from the folder above. Refreshed{' '}
              {formatRelative(data.stats.refreshedAt)}; kept for about 10
              minutes, and refreshed when you apply a change.
            </p>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">
              Stats refreshed {formatRelative(data.stats.refreshedAt)}. Cached
              for ~10 minutes; busts on Apply.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ─── view helpers ─────────────────────────────────────────────────────

/** "a week" for the usual 7-day margin, "N days" otherwise (the margin
 * shrinks when the age limit is set to its minimum). */
function marginLabel(days: number): string {
  if (days === 7) return 'a week'
  return days === 1 ? '1 day' : `${days} days`
}

function renderLifecycle(
  lifecycle: AdminRunArtifactsRetention['lifecycle'],
  intendedMaxAgeDays: number,
): React.ReactNode {
  if ('skipped' in lifecycle && lifecycle.skipped === 'disk-store') {
    return (
      <span className="text-muted-foreground italic">
        Disk store — no lifecycle rule
      </span>
    )
  }
  if ('error' in lifecycle) {
    return (
      <span className="text-destructive">
        Failed to read lifecycle: {lifecycle.error}
      </span>
    )
  }
  // After the two narrowing branches above, this is the configured-state
  // variant. TypeScript can't infer that from the discriminator shape
  // (no shared discriminant key), so re-check explicitly.
  if (!('configured' in lifecycle)) return null
  if (!lifecycle.configured) {
    return (
      <span className="text-muted-foreground">
        No rule applied yet — hit Apply to write one
      </span>
    )
  }
  const applied = lifecycle.appliedMaxAgeDays
  const drifted =
    applied !== undefined && applied !== intendedMaxAgeDays
  if (!lifecycle.enabled) {
    return (
      <span className="text-amber-500">
        Rule present but disabled — re-Apply to enable
      </span>
    )
  }
  if (lifecycle.nonStandardExpiration) {
    return (
      <span className="text-amber-500">
        Rule present but uses a non-standard expiration shape (likely
        an out-of-band Date-based edit) — re-Apply to normalise to
        Days
      </span>
    )
  }
  if (applied === undefined) {
    return (
      <span className="text-destructive">
        Rule present but reports no day count — re-Apply to repair
      </span>
    )
  }
  if (drifted) {
    return (
      <span className="text-amber-500">
        Deletes objects &gt; {applied} days (drift from intended{' '}
        {intendedMaxAgeDays}d — re-Apply to sync)
      </span>
    )
  }
  return <>Deletes objects &gt; {applied} days ✓</>
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  )
  const v = bytes / Math.pow(1024, i)
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return 'unknown'
  const now = Date.now()
  const diffSec = Math.max(0, Math.round((now - then) / 1000))
  if (diffSec < 60) return `${diffSec}s ago`
  const min = Math.round(diffSec / 60)
  if (min < 60) return `${min} min ago`
  const hr = Math.round(min / 60)
  if (hr < 48) return `${hr} hr ago`
  const days = Math.round(hr / 24)
  return `${days} days ago`
}

